import { BaseQueryFn, createApi, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { getSettings, SettingsState } from '..';
import { ChangePassword, ResetPassword, User, UsersResponse } from '../models/auth';
import { fetchQueryWithToken } from './auth.helpers';

const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const settings = getSettings(api.getState() as SettingsState);
  const urlEnd = typeof args === 'string' ? args : args.url;
  const adjustedUrl = `${settings?.servers.serverAuth ?? ''}/api/v1/manager/${urlEnd}`;
  return fetchQueryWithToken(typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl }, api, extraOptions);
};

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['User'],
  endpoints: builder => ({
    getUsers: builder.query<User[], void>({
      query: () => ({
        url: 'user',
      }),
      providesTags: ['User'],
      transformResponse: (response: UsersResponse) => response?.data,
    }),
    createUser: builder.mutation<User, Partial<User>>({
      query: body => ({
        url: 'user',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    updateUser: builder.mutation<User, User>({
      query: body => ({
        url: `user/${body.userID}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    deleteUser: builder.mutation<void, User['userID']>({
      query: id => ({
        url: `user/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
    changePassword: builder.mutation<void, ChangePassword>({
      query: body => ({
        url: 'user/password/change',
        method: 'PUT',
        body,
      }),
    }),
    resetPassword: builder.mutation<void, ResetPassword>({
      query: body => ({
        url: 'user/password/reset',
        method: 'PUT',
        body,
      }),
    }),
  }),
});

export const {
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useChangePasswordMutation,
  useResetPasswordMutation,
} = usersApi;
